<template>
  <v-sheet>
    <gg-toolbar v-if="toolbar" @close="$emit('close')">
      <slot name="header"></slot>
    </gg-toolbar>
    <gg-card-text :class="{ 'px-0': !compact, 'pa-0': noPadding }">
      <template v-if="paypal">
        <v-container class="pa-0">
          <v-row>
            <v-col cols="12">
              <p>Choose your payment method.</p>
              <v-radio-group v-model="gateway" @change="emitGateway">
                <v-radio
                  label="Credit Card"
                  value="credit_card"
                />
                <v-radio
                  label="PayPal"
                  value="paypal"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <template v-if="isCard && !onlyNew">
        <v-container fluid class="pa-0">
          <v-row>
            <v-col cols="12">
              <p class="mb-1">{{ cardSelectionLabel || cardTitle }}</p>
              <v-radio-group
                v-if="!cardSelect"
                v-model="isCardNew"
                @change="switchCard">
                <v-radio
                  :label="existingCardLabel"
                  :value="false"
                  :disabled="!isCardAvailable"
                />
                <v-radio
                  label="Use a new card"
                  :value="true"
                />
              </v-radio-group>
              <gg-select
                v-else
                v-model="isCardNew"
                @change="switchCard"
                item-value="value"
                item-text="label"
                :items="cardSelectItems"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex align-center">
                    <c-c-icon v-if="item.icon" :card-type="item.CCType" />
                    <span class="my-auto pl-2">
                      {{ item.label }}
                      <v-chip small color="red" class="white--text" v-if="isCreditCardExpired && item.disabled">Expired</v-chip>
                    </span>
                  </div>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <c-c-icon v-if="item.icon" :card-type="item.CCType" />
                    <span class="my-auto pl-2">{{ item.label }}</span>
                  </div>
                </template>
              </gg-select>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <template v-if="isCard">
        <v-form data-vv-scope="card-payment" @submit.prevent>
          <p v-if="isCardNew" class="pb-3">
            Your new card information will be saved on your account for future payments.
          </p>
          <v-row>
            <template v-if="isCardNew">
              <v-col cols="12" :sm="compact ? 6 : 4" class="py-0">
                <gg-text-field
                  v-model="model.cardnum"
                  label="Card Number"
                  aria-label="Card Number"
                  ref="cc_number"
                  name="CardNumber"
                  outlined
                  v-validate="rules.cardnum"
                  data-vv-as="Card Number"
                  :error-messages="errors.collect('card-payment.CardNumber')"
                  v-mask="masks.cardnum"
                  :append-icon="cardIcon"
                  @input="emitCard"
                />
              </v-col>
              <v-col cols="12" :sm="compact ? 3 : 4" class="py-0">
                <gg-text-field
                  v-model="model.expdate"
                  label="Expiry Date"
                  aria-label="Expiry Date"
                  ref="cc_expdate"
                  name="ExpiryDate"
                  outlined
                  v-validate="rules.expdate"
                  data-vv-as="Expiry Date"
                  :error-messages="errors.collect('card-payment.ExpiryDate')"
                  v-mask="masks.expdate"
                  placeholder="MM/YY"
                  return-masked-value
                  @change="emitCard"
                />
              </v-col>
            </template>

            <v-col cols="12" :sm="compact ? 3 : 4" class="py-0">
              <gg-text-field
                v-model="model.cardnum_cvv"
                ref="cc_cvv"
                class="gg-input cardnum-cvv"
                name="CVV/CVC"
                label="CVV/CVC"
                aria-label="CVV/CVC"
                dense
                outlined
                v-validate="rules.cardnum_cvv"
                :error-messages="errors.collect('card-payment.CVV/CVC')"
                @input="emitCard">
                <template v-slot:append>
                  <v-btn icon @click="toggleCvcHint" aria-label="What is CVV/CVC?" :ripple="false">
                    <v-icon>mdi-help-circle-outline</v-icon>
                  </v-btn>
                </template>
              </gg-text-field>
            </v-col>

            <v-col cols="12" v-if="showCvcHint">
              <v-alert
                color="black"
                outlined
                transition="slide-y-transition">
                <p>
                  <b>Visa®, Mastercard®, and Discover®
                    cardholders:</b><br>
                  Turn your card over and look at the signature box.
                  You should see either the entire
                  16-digit
                  credit card number or just the last four digits
                  followed by a special 3-digit code. This
                  3-digit code is your CVV number / Card Security
                  Code.
                </p>
                <p class="mb-0">
                  <b>American Express® cardholders:</b><br>
                  Look for the 4-digit code printed on the front of
                  your card just above and to the right of
                  your main credit card number. This 4-digit code is
                  your Card Identification Number (CID).
                  The CID is the four-digit code printed just above
                  the Account Number.
                </p>
              </v-alert>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </gg-card-text>
    <v-card-actions class="justify-content-end pb-4 pr-4">
      <slot name="actions"></slot>
    </v-card-actions>
  </v-sheet>
</template>

<script>
import { Validator } from 'vee-validate'
import Payment from 'payment'
import { mask } from 'vue-the-mask'
import { mapGetters } from 'vuex'
import { isAfter, parse, format } from 'date-fns'
import CCIcon from './CCIcon'

const cardValidator = {
  getMessage (field, args) {
    return `${field} value is invalid.`
  },
  validate (value, args) {
    return Payment.fns.validateCardNumber(value)
  },
}
const expiryValidator = {
  getMessage (field, args) {
    return `${field} value is invalid.`
  },
  validate (value, args) {
    return Payment.fns.validateCardExpiry(value)
  },
}
const cvcValidator = {
  getMessage (field, args) {
    return `${field} value is invalid.`
  },
  validate (value, args) {
    return Payment.fns.validateCardCVC(value)
  },
}

export default {
  name: 'PaymentGateway',
  inject: ['$validator'],
  directives: {
    mask,
  },
  components: {
    CCIcon
  },
  props: {
    toolbar: {
      type: Boolean,
      default: false,
    },
    paypal: {
      type: Boolean,
      default: false
    },
    onlyNew: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    },
    cardSelect: {
      type: Boolean,
      default: false
    },
    cardSelectionLabel: {
      type: String,
      default: ''
    },
    cardType: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    gateway: 'credit_card',
    isCardNew: false,
    isCardAvailable: false,
    model: {
      cardnum: '',
      expdate: '',
      cardnum_cvv: ''
    },
    rules: {
      cardnum: {
        required: true,
        card: true,
      },
      expdate: {
        required: true,
        expiry: true,
      },
      cardnum_cvv: {
        required: true,
        cvc: true,
      },
    },
    showCvcHint: false,
    masks: {
      cardnum: '####-####-####-####',
      expdate: '##/##'
    }
  }),
  computed: {
    ...mapGetters(['SessionProfile']),
    isCard () {
      return this.gateway === 'credit_card'
    },
    cardIcon () {
      return 'mdi-credit-card'
    },
    cardTitle () {
      switch (this.isCardNew) {
        case true:
          return 'Enter in your new credit card.'
        case false:
          return 'Confirm your existing CVV.'
        default:
          return 'Enter in your new credit card.'
      }
    },
    existingCardLabel () {
      if (this.SessionProfile.card_lastfour !== undefined) {
        const date = parse(this.SessionProfile.card_expdate, 'MMyy', new Date())
        const expDate = format(date, 'MM/yyyy')
        return `**** **** **** ${this.SessionProfile.card_lastfour} (${expDate})`
      } else {
        return `Use your existing card`
      }
    },
    cardSelectItems () {
      const items = []
      if (this.SessionProfile.card_lastfour !== undefined) {
        items.push({
          label: this.existingCardLabel,
          value: false,
          icon: true,
          CCType: this.SessionProfile.card_type,
          disabled: this.isCreditCardExpired
        })
      }
      items.push({ label: 'Add New Card', value: true, icon: true })
      return items
    },
    isCreditCardExpired () {
      const currentDate = new Date()

      const expDateString = this.SessionProfile.card_expdate

      const parsedExpDate = parse(`${expDateString}`, 'MMyy', new Date())

      return isAfter(currentDate, parsedExpDate)
    }
  },
  mounted () {
    Validator.extend('card', cardValidator)
    Validator.extend('cvc', cvcValidator)
    Validator.extend('expiry', expiryValidator)
    if (!this.onlyNew) {
      this.fillCard()
    } else {
      this.isCardNew = true
    }
    this.emitGateway()
    this.applyInputValidation()

    if (this.isCreditCardExpired) {
      this.isCardNew = true
    }
  },
  methods: {
    async applyInputValidation () {
      await this.$nextTick(() => {
        if (this.isCardNew) {
          Payment.formatCardNumber(this.$refs.cc_number.$el)
          Payment.formatCardExpiry(this.$refs.cc_expdate.$el)
        }
        Payment.formatCardCVC(this.$refs.cc_cvv.$el)
      })
    },
    fillCard () {
      if (this.SessionProfile.card_lastfour !== undefined) {
        this.isCardAvailable = true
        this.isCardNew = false
        this.model.cardnum = `${this.SessionProfile.card_type} - ${this.SessionProfile.card_lastfour}`
        this.model.expdate = this.SessionProfile.card_expdate
      } else {
        this.isCardAvailable = false
        this.isCardNew = true
        this.model.cardnum = null
        this.model.expdate = null
      }
      this.$emit('card-switch', this.isCardNew)
    },
    toggleCvcHint () {
      this.showCvcHint = !this.showCvcHint
    },
    switchCard () {
      if (this.isCardNew) {
        this.model.cardnum = ''
        this.model.expdate = ''
        this.model.cardnum_cvv = ''
      } else {
        if (this.SessionProfile.card_lastfour === undefined) {
          this.model.cardnum = ''
          this.model.expdate = ''
        } else {
          this.model.cardnum = `${this.SessionProfile.card_type} - ${this.SessionProfile.card_lastfour}`
          this.model.expdate = this.SessionProfile.card_expdate
        }
      }
      this.$emit('card-switch', this.isCardNew)
    },
    emitGateway () {
      this.$emit('gateway', this.gateway)
    },
    emitCard () {
      this.$emit('card', this.model)
    },
    async validateForm () {
      if (this.isCard) {
        const valid = await this.$validator.validateAll('card-payment')
        return valid
      }
      // TODO: validation for other payment methods/forms
      const valid = await this.$validator.validateAll()
      return valid
    },
    clearForm () {
      this.model = {
        cardnum: '',
        expdate: '',
        cardnum_cvv: ''
      }

      this.$nextTick(() => {
        this.$validator.reset()
      })
    }
  },
}
</script>

<style lang="scss">
  .cardnum-cvv .v-input__append-inner {
    margin-top: 2px !important;
  }

  .cardnum-cvv .v-input__slot {
    padding-right: 4px !important;
  }
</style>
